import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { AlertTriangle, CheckCircle, XCircle, Loader2, MessageCircleQuestion } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SeoHead } from '@/components/SeoHead';

function ReportProblemLink({ userId, inviteCode }: { userId?: string; inviteCode?: string }) {
  const { t } = useLanguage();
  
  const getSubject = () => encodeURIComponent('💯 drinkov: problem');
  const getBody = () => {
    const lines = [
      'Screen: Join challenge',
      userId ? `User ID: ${userId}` : null,
      inviteCode ? `Invite Code: ${inviteCode}` : null,
      '',
      'Describe the issue:',
      '',
    ].filter(Boolean);
    return encodeURIComponent(lines.join('\n'));
  };
  
  return (
    <a 
      href={`mailto:peter@tugedr.com?subject=${getSubject()}&body=${getBody()}`}
      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
    >
      <MessageCircleQuestion className="h-3.5 w-3.5" />
      {t('reportProblem')}
    </a>
  );
}

type JoinState = 
  | 'loading' 
  | 'invalid_code' 
  | 'already_member' 
  | 'has_other_group' 
  | 'joining' 
  | 'success'
  | 'blocked';

const PENDING_INVITE_KEY = 'pending_invite_token';

export default function Join() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, membership, group, loading: authLoading, refreshMembership } = useAuth();
  const { t } = useLanguage();
  
  const [state, setState] = useState<JoinState>('loading');
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [targetGroupId, setTargetGroupId] = useState<string | null>(null);
  
  // Support both ?token= (new) and ?code= (backward compat)
  const inviteToken = searchParams.get('token')?.toUpperCase().trim() || '';
  const legacyCode = searchParams.get('code')?.toUpperCase().trim() || '';
  const effectiveCode = inviteToken || legacyCode;
  const isTokenMode = !!inviteToken;

  useEffect(() => {
    // If user is not authenticated, store the code and redirect to auth
    if (!authLoading && !user) {
      if (effectiveCode) {
        // Store both the code and whether it's a token
        localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify({
          code: effectiveCode,
          isToken: isTokenMode,
        }));
      }
      navigate('/auth?mode=signup', { replace: true });
      return;
    }

    // Wait for auth to finish loading
    if (authLoading || !user) return;

    // Check for pending invite after auth
    const pendingRaw = localStorage.getItem(PENDING_INVITE_KEY);
    let pendingCode = '';
    let pendingIsToken = false;
    
    if (pendingRaw) {
      try {
        const parsed = JSON.parse(pendingRaw);
        pendingCode = parsed.code || '';
        pendingIsToken = parsed.isToken || false;
      } catch {
        // Legacy format: plain string (old ?code= links)
        pendingCode = pendingRaw;
        pendingIsToken = false;
      }
      localStorage.removeItem(PENDING_INVITE_KEY);
      
      if (!effectiveCode && pendingCode) {
        const param = pendingIsToken ? 'token' : 'code';
        navigate(`/join?${param}=${pendingCode}`, { replace: true });
        return;
      }
    }

    const codeToUse = effectiveCode || pendingCode;
    const useTokenMode = isTokenMode || pendingIsToken;

    if (!codeToUse) {
      setState('invalid_code');
      return;
    }

    validateAndJoin(codeToUse, useTokenMode);
  }, [authLoading, user, effectiveCode, membership]);

  const validateAndJoin = async (code: string, useToken: boolean) => {
    setState('loading');

    try {
      let groupData: { id: string; status: string; challenge_start: string; challenge_end: string } | null = null;

      if (useToken) {
        // Token-based lookup
        const { data, error } = await supabase
          .rpc('lookup_group_by_invite_token', { p_token: code })
          .maybeSingle();

        if (!error && data) {
          groupData = data;
        }
      }

      // Fall back to invite code lookup (backward compat)
      if (!groupData) {
        const { data, error } = await supabase
          .rpc('lookup_group_by_invite_code', { code })
          .maybeSingle();

        if (!error && data) {
          groupData = data;
        }
      }

      if (!groupData) {
        setState('invalid_code');
        return;
      }

      if (groupData.status !== 'active' || new Date(groupData.challenge_end) <= new Date()) {
        setState('invalid_code');
        return;
      }

      setTargetGroupId(groupData.id);

      // Check if user is blocked from this group (generic error, no reveal)
      const { data: isBlocked } = await supabase
        .rpc('is_blocked_from_group', { target_group_id: groupData.id });

      if (isBlocked) {
        // Show generic error without revealing block status
        setState('blocked');
        return;
      }

      // Check if user already has a membership
      if (membership && group) {
        // Check if same group
        if (group.id === groupData.id) {
          setState('already_member');
          return;
        }
        
        // User is in a different active group
        setState('has_other_group');
        return;
      }

      // User has no active membership, join directly
      await joinGroup(groupData.id);
    } catch (error) {
      console.error('Error validating invite:', error);
      setState('invalid_code');
    }
  };

  const joinGroup = async (groupId: string) => {
    setState('joining');

    try {
      const { error: membershipError } = await supabase
        .from('memberships')
        .insert({
          user_id: user!.id,
          group_id: groupId,
          role: 'member',
        });

      if (membershipError) {
        if (membershipError.message.includes('duplicate key')) {
          setState('already_member');
        } else {
          console.error('Error joining group:', membershipError);
          toast.error(t('onboardingJoinFailed'));
          setState('invalid_code');
        }
        return;
      }

      await refreshMembership();
      setState('success');
      toast.success(t('onboardingJoinSuccess'));
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
    } catch (error) {
      console.error('Error joining group:', error);
      toast.error(t('authUnexpectedError'));
      setState('invalid_code');
    }
  };

  const handleLeaveAndJoin = async () => {
    if (!membership || !targetGroupId) return;
    
    setIsLeaving(true);
    
    try {
      // Leave current group
      const { error: leaveError } = await supabase
        .from('memberships')
        .delete()
        .eq('id', membership.id);

      if (leaveError) {
        toast.error(t('leaveChallengeFailed'));
        setIsLeaving(false);
        return;
      }

      // Join new group
      await refreshMembership();
      await joinGroup(targetGroupId);
    } catch (error) {
      console.error('Error leaving and joining:', error);
      toast.error(t('authUnexpectedError'));
      setIsLeaving(false);
    }
    
    setShowLeaveDialog(false);
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  if (state === 'loading' || state === 'joining') {
    return (
      <>
      <SeoHead
        title="Join challenge — Drinks in 365 Days"
        description="Accept your invite and join a private 100 drinks in 365 days challenge with friends."
        path="/join"
        noindex
      />
      <div className="min-h-screen-safe flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-muted-foreground">
                {state === 'loading' ? t('loading') : t('onboardingJoining')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      </>
    );
  }

  if (state === 'invalid_code') {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-xl font-serif">{t('joinInvalidCodeTitle')}</CardTitle>
            <CardDescription>{t('joinInvalidCodeDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleGoHome} className="w-full">
              {t('joinGoHome')}
            </Button>
          </CardContent>
          <CardFooter className="justify-center">
            <ReportProblemLink userId={user?.id} inviteCode={effectiveCode} />
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (state === 'already_member') {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-xl font-serif">{t('joinAlreadyMemberTitle')}</CardTitle>
            <CardDescription>{t('joinAlreadyMemberDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleGoHome} className="w-full">
              {t('joinGoToDashboard')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'has_other_group') {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-warning" />
            </div>
            <CardTitle className="text-xl font-serif">{t('joinHasOtherGroupTitle')}</CardTitle>
            <CardDescription>{t('joinHasOtherGroupDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              onClick={() => setShowLeaveDialog(true)} 
              variant="destructive" 
              className="w-full"
            >
              {t('joinLeaveAndJoin')}
            </Button>
            <Button onClick={handleGoHome} variant="outline" className="w-full">
              {t('cancel')}
            </Button>
          </CardContent>
        </Card>

        <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('joinConfirmLeaveTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('joinConfirmLeaveDesc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isLeaving}>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleLeaveAndJoin}
                disabled={isLeaving}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isLeaving ? t('leaveChallengeLeaving') : t('joinConfirmLeaveAction')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  if (state === 'blocked') {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-xl font-serif">{t('joinBlockedTitle')}</CardTitle>
            <CardDescription>{t('joinBlockedDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleGoHome} className="w-full">
              {t('joinGoHome')}
            </Button>
          </CardContent>
          <CardFooter className="justify-center">
            <ReportProblemLink userId={user?.id} inviteCode={effectiveCode} />
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-xl font-serif">{t('joinSuccessTitle')}</CardTitle>
            <CardDescription>{t('joinSuccessDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground text-sm">
              {t('joinRedirecting')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
